import { API_URL, APIError, ApiResponse, getHeaders } from '../lib/_api-helpers';
import * as React from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

type UseGetRequestData<T> = {
    refetchData: () => Promise<void>;
    data: T | null;
    loading: boolean;
    errors: APIError[];
    called: boolean;
    unauthenticated?: boolean;
};

export default function useGetRequest<T>(
    endpoint: string,
    delay?: boolean,
    unauthenticated?: boolean,
): UseGetRequestData<T> {
    const navigate = useNavigate();
    const url = `${API_URL}/${endpoint}`;

    const [data, setData] = React.useState<T | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [errors, setErrors] = React.useState<APIError[]>([]);
    const [called, setCalled] = React.useState(false);

    React.useEffect(() => {
        if (delay) return;
        refetchData().then();
    }, [delay]); // eslint-disable-line

    const refetchData = async () => {
        setLoading(true);

        try {
            let headers: HeadersInit = {};

            if (!unauthenticated) {
                try {
                    const session = await fetchAuthSession();
                    const token = session.tokens?.accessToken?.toString();

                    if (!token) navigate('/signin');
                    headers = getHeaders(token);
                } catch (err) {
                    navigate('/signin');
                    return;
                }
            }

            const response = await fetch(url, { headers });
            const json: ApiResponse<T> = await response.json();

            setData(json.value ?? null);
            setErrors(json.errors ?? []);
        } catch (error) {
            setData(null);
            setErrors([{ field: 'global', code: 'serviceError' }]);
        } finally {
            setLoading(false);
            setCalled(true);
        }
    };

    return {
        refetchData,
        data,
        loading,
        errors,
        called,
    };
}
